import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Button from 'components/buttons/Button';
import RenderFormInput from 'components/forms/RenderFormInput';
import RenderDateTimePicker from 'components/forms/RenderDateTimePicker';
import RenderSelectInput from 'components/forms/RenderSelectInput';
import { requiredValidator, requiredOptionValidator, urlValidator, handleBlur } from 'utils/validators';
import Label from 'components/forms/Label';
import { resultsToOptions } from 'utils/misc';
import { Field, reduxForm, getFormValues, change } from 'redux-form';
import ConfirmationModal from 'components/modals/ConfirmationModal';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/styles';
import { createMention, editMention } from 'redux/actions/coverageActions';
import { getAccounts } from 'redux/actions/accountActions';
import { getOpportunities } from 'redux/actions/opportunityActions';
import { getSequences } from 'redux/actions/sequenceActions';
import { getOutletNameUrl } from 'constants/opportunity';
import { ARTICLE_TYPE_OPTIONS, FOLLOW_OPTIONS } from 'constants/coverage';
import { getCampaigns } from 'redux/actions/campaignActions';
import { timelineStepRow } from 'themes/colors';
import { getOutlets } from 'redux/actions/contactActions';
import { capitalize, isEmpty } from 'lodash';
import opportunitiesSelector from 'selectors/opportunitiesSelector';
import { OPPORTUNITY_STAGES } from '../../constants/opportunity';
import { patchOpportunity } from '../../redux/actions/opportunityActions';


const styles = theme => ({
  groupTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: theme.palette.secondary[900],
  },
  group: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    borderRadius: '0.375rem',
    background: timelineStepRow,
    marginBottom: '1rem',

    '& > label:first-child': {
      marginTop: 0,
    },
  },
  twoColumnGroup: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '0.75rem',
  },
  selectGroup: {
    flex: 1,
  },
  btnContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    gap: '1rem',
  },
  nestedInput: {
    flex: 1,

    '& > .react-datepicker-wrapper': {
      width: '100%',
    },
  },
});


const CoverageForm = ({
  initialValues, toggle, createMentionAction, editMentionAction, opportunities,
  accounts, outlets, campaigns, sequences, classes, getOpportunitiesAction, getAccountsAction, getOutletsAction,
  getCampaignsAction, getSequencesAction, formValues, dispatch, invalid, pristine, submitting, patchOpportunityAction,
}) => {
  const [areYouSureOpen, setAreYouSureOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isEdit = initialValues.articleURL;
  const opportunity = opportunities
    && opportunities.results
    && formValues && formValues.opportunity
    && opportunities.results.find(o => o.id === formValues.opportunity.value);

  const handleLoadOutletOptions = async (search) => {
    await getOutletsAction({ search, account__id: formValues.account.value });
  };

  const handleLoadAccountOptions = async (search) => {
    await getAccountsAction({ search });
  };

  const handleLoadOpportunitiesOptions = async (search) => {
    await getOpportunitiesAction({ search, account_id: [formValues.account.value].join(',') });
  };

  const handleLoadCampaignOptions = async (search) => {
    await getCampaignsAction({ search, account__id: formValues.account.value });
  };

  const handleLoadSequenceOptions = async (search) => {
    await getSequencesAction(formValues.campaign.value, { search });
  };

  useEffect(() => {
    (async () => {
      await handleLoadAccountOptions();
      if (initialValues.account) {
        dispatch(change('add_media_hit_form', 'account', initialValues.account));
      }
      if (initialValues.campaign) {
        dispatch(change('add_media_hit_form', 'campaign', initialValues.campaign));
      }
      if (initialValues.sequence) {
        dispatch(change('add_media_hit_form', 'sequence', initialValues.sequence));
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (formValues && formValues.account && formValues.account.value) {
        await handleLoadOutletOptions();
        await handleLoadCampaignOptions();
        await handleLoadOpportunitiesOptions();
        if (!initialValues) {
          dispatch(change('add_media_hit_form', 'campaign', ''));
          dispatch(change('add_media_hit_form', 'sequence', ''));
          dispatch(change('add_media_hit_form', 'opportunity', ''));
        }
      }
    })();
  }, [formValues && formValues.account && formValues.account.value]);

  useEffect(() => {
    (async () => {
      if (formValues && formValues.campaign && formValues.campaign.value) {
        await handleLoadSequenceOptions();
        if (!initialValues) {
          dispatch(change('add_media_hit_form', 'sequence', ''));
        }
      }
    })();
  }, [formValues && formValues.campaign && formValues.campaign.value]);

  const handleSubmit = async () => {
    setLoading(true);
    const submitFunc = isEdit ? editMentionAction : createMentionAction;
    const outlet = outlets.results.find(o => o.id === formValues.outlet.value);
    // eslint-disable-next-line no-param-reassign
    formValues.outlet = { ...formValues.outlet, ...outlet };
    await submitFunc(formValues);
    setLoading(false);
    toggle();
  };

  const handlePreSubmit = async (event) => {
    event.preventDefault();
    if (opportunity && opportunity.stage !== OPPORTUNITY_STAGES.published) {
      // return prompt for confirmation on moving to published
      return setAreYouSureOpen(true);
    }
    return handleSubmit();
  };

  const handleConfirmPublish = async () => {
    await patchOpportunityAction(opportunity, { stage: OPPORTUNITY_STAGES.published });
    handleSubmit();
  };

  const handleAccountChange = () => {
    dispatch(change(
      'add_media_hit_form',
      'opportunity',
      null,
    ));
    dispatch(change(
      'add_media_hit_form',
      'campaign',
      null,
    ));
    dispatch(change(
      'add_media_hit_form',
      'sequence',
      null,
    ));
    dispatch(change(
      'add_media_hit_form',
      'outlet',
      null,
    ));
  };

  const handleArticleTypeChange = ({ value, label }) => {
    dispatch(change(
      'add_media_hit_form',
      'article_type',
      { label, value },
    ));
  };
  const handleFollowChange = ({ value, label }) => {
    dispatch(change(
      'add_media_hit_form',
      'follow_status',
      { label, value },
    ));
  };

  const handleOpportunityChange = ({ value }) => {
    const opp = opportunities.results.find(o => o.id === value);
    if (opp.sequence) {
      dispatch(change(
        'add_media_hit_form',
        'campaign',
        { label: opp.sequence.campaign.name, value: opp.sequence.campaign.id },
      ));
      dispatch(change(
        'add_media_hit_form',
        'sequence',
        { label: opp.sequence.name, value: opp.sequence.id },
      ));
    }
    if (opp.outlet) {
      dispatch(change(
        'add_media_hit_form',
        'outlet',
        { label: opp.outlet.name, value: opp.outlet.id },
      ));
    }
  };

  const submitLabel = isEdit ? 'Save' : 'Add';
  const currentStage = opportunity && opportunity.stage;

  return (
    <>
      <form onSubmit={handlePreSubmit}>
        <span className={classes.groupTitle}>Hit</span>
        <fieldset className={classes.group}>
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Article URL *</Label>
          <Field
            maxLength="255"
            name="articleURL"
            component={RenderFormInput}
            type="text"
            onBlur={handleBlur}
            validate={[requiredValidator, urlValidator]}
          />
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Title *</Label>
          <Field
            maxLength="255"
            name="title"
            component={RenderFormInput}
            type="text"
            onBlur={handleBlur}
            validate={[requiredValidator]}
          />
          <div className={classes.twoColumnGroup}>
            <div className={classes.selectGroup}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <Label>Date</Label>
              <Field
                className="date-picker"
                name="date"
                component={RenderDateTimePicker}
                props={{ className: classes.nestedInput }}
                maxDate={new Date()}
                showTimeSelect
                defaultToNoon
                timezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
              />
            </div>
            <div className={classes.selectGroup}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <Label>Author</Label>
              <Field
                maxLength="255"
                name="author"
                component={RenderFormInput}
                type="text"
                onBlur={handleBlur}
                props={{ className: classes.nestedInput }}
              />
            </div>
          </div>
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Notes</Label>
          <Field
            maxLength="255"
            name="note"
            component={RenderFormInput}
            type="text"
            onBlur={handleBlur}
          />
          <Label>Article type</Label>
          <Field
            name="article_type"
            component={RenderSelectInput}
            onBlur={handleBlur}
            onChange={handleArticleTypeChange}
            options={ARTICLE_TYPE_OPTIONS || []}
            // onInputChange={input => handleLoadAccountOptions(input, true)}
          />
          <Label>Follow</Label>
          <Field
            name="follow_status"
            component={RenderSelectInput}
            onBlur={handleBlur}
            onChange={handleFollowChange}
            options={FOLLOW_OPTIONS || []}
            // onInputChange={input => handleLoadAccountOptions(input, true)}
          />
        </fieldset>
        <span className={classes.groupTitle}>Opportunity</span>
        <fieldset className={classes.group}>
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Account *</Label>
          <Field
            name="account"
            component={RenderSelectInput}
            onBlur={handleBlur}
            onChange={handleAccountChange}
            validate={[requiredValidator]}
            options={resultsToOptions(accounts.results, 'name') || []}
            onInputChange={input => handleLoadAccountOptions(input, true)}
          />
          {/* eslint-disable jsx-a11y/label-has-for */}
          <Label>Opportunity</Label>
          <Field
            name="opportunity"
            component={RenderSelectInput}
            onBlur={handleBlur}
            onChange={handleOpportunityChange}
            options={resultsToOptions(opportunities.results, 'name') || []}
            onInputChange={input => handleLoadOpportunitiesOptions(input, true)}
            disabled={formValues && isEmpty(formValues.account)}
          />
          <div className={classes.twoColumnGroup}>
            <div className={classes.selectGroup}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <Label>Campaign</Label>
              <Field
                name="campaign"
                component={RenderSelectInput}
                options={resultsToOptions(campaigns.results, 'name') || []}
                onInputChange={input => handleLoadCampaignOptions(input, true)}
                onBlur={handleBlur}
                disabled={formValues && (isEmpty(formValues.account) || !isEmpty(formValues.opportunity))}
              />
            </div>
            <div className={classes.selectGroup}>
              {/* eslint-disable jsx-a11y/label-has-for */}
              <Label>Sequence</Label>
              <Field
                name="sequence"
                onBlur={handleBlur}
                component={RenderSelectInput}
                options={resultsToOptions(sequences.results, 'name') || []}
                onInputChange={input => handleLoadSequenceOptions(input, true)}
                disabled={formValues && (isEmpty(formValues.campaign) || !isEmpty(formValues.opportunity))}
              />
            </div>
          </div>
          <div className={classes.selectGroup}>
            {/* eslint-disable jsx-a11y/label-has-for */}
            <Label>Outlet *</Label>
            <Field
              name="outlet"
              onBlur={handleBlur}
              validate={[requiredOptionValidator]}
              component={RenderSelectInput}
              options={resultsToOptions(outlets.results, getOutletNameUrl) || []}
              onInputChange={input => handleLoadOutletOptions(input, true)}
              disabled={formValues && isEmpty(formValues.account)}
            />
          </div>
        </fieldset>
        <div className={classes.btnContainer}>
          <Button
            type="button"
            color="secondary"
            variant="text"
            onClick={toggle}
            disabled={loading || submitting}
          >Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            type="submit"
            loading={loading}
            disabled={pristine || submitting || invalid || loading}
          >{submitLabel}
          </Button>
        </div>
      </form>
      <ConfirmationModal
        open={areYouSureOpen}
        onBackdropClick={() => setAreYouSureOpen(false)}
        title="Coverage linked to the Opportunity"
        confirmLabel="Yes"
        cancelLabel="No"
        onConfirmClick={handleConfirmPublish}
        onCancelClick={handleSubmit}
      >
        Would you like the stage (currently &quot;{capitalize(currentStage)}&quot;) updated to &quot;Published&quot;?
      </ConfirmationModal>
    </>
  );
};


CoverageForm.propTypes = {
  classes: PropTypes.element.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  formValues: PropTypes.element,
  initialValues: PropTypes.element,
  createMentionAction: PropTypes.func.isRequired,
  editMentionAction: PropTypes.func.isRequired,
  accounts: PropTypes.element.isRequired,
  opportunities: PropTypes.element.isRequired,
  campaigns: PropTypes.element.isRequired,
  sequences: PropTypes.element.isRequired,
  outlets: PropTypes.element.isRequired,
  getAccountsAction: PropTypes.func.isRequired,
  getOpportunitiesAction: PropTypes.func.isRequired,
  getCampaignsAction: PropTypes.func.isRequired,
  getSequencesAction: PropTypes.func.isRequired,
  getOutletsAction: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

CoverageForm.defaultProps = {
  formValues: null,
  initialValues: {},
};

export default compose(
  connect(
    (state, props) => {
      let initialValues = {
        articleURL: null,
        title: null,
        date: null,
        author: null,
        account: null,
        campaign: null,
        sequence: null,
        note: null,
        opportunity: null,
        article_type: null,
        follow_status: null,
      };
      if (!isEmpty(props.initialValues)) {
        initialValues = {
          ...initialValues,
          ...props.initialValues,
        };
      }

      return {
        outlets: state.outlets,
        accounts: state.accounts,
        opportunities: opportunitiesSelector(state.opportunities),
        campaigns: state.campaigns,
        sequences: state.sequences,
        formValues: getFormValues('add_media_hit_form')(state),
        initialValues,
      };
    },
    {
      getOutletsAction: getOutlets,
      getAccountsAction: getAccounts,
      getOpportunitiesAction: getOpportunities,
      getCampaignsAction: getCampaigns,
      getSequencesAction: getSequences,
      createMentionAction: createMention,
      editMentionAction: editMention,
      patchOpportunityAction: patchOpportunity,
    },
  ),
  withStyles(styles),
  reduxForm({
    form: 'add_media_hit_form',
  }),
)(CoverageForm);
